import React, { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import {
  learnerApi,
  type ExamAttemptListItem,
  type UserActivitySummary,
  type UserFavouriteRow,
} from '../../../api/learner';
import { Loader2, ClipboardList, Flame, Bookmark } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { toast } from 'sonner';
import { usersApi, type UserEngagementInfo } from '../../../api/users';
import { StatCard } from './userTabShared';

interface Props {
  userId: string;
  isDark: boolean;
}

export function UserActivityTab({ userId, isDark }: Props) {
  const [tab, setTab] = useState<'summary' | 'favourites'>('summary');
  const [summary, setSummary] = useState<UserActivitySummary | null>(null);
  const [favourites, setFavourites] = useState<UserFavouriteRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [engagement, setEngagement] = useState<UserEngagementInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    usersApi
      .getUserEngagement(userId)
      .then((res) => {
        if (!cancelled) setEngagement(res);
      })
      .catch(() => {
        // Игры/AI — дополнительный блок, без него вкладка всё равно полезна
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await learnerApi.userActivity(userId);
        if (!cancelled) setSummary(data);
      } catch (e: any) {
        if (!cancelled) toast.error(e?.response?.data?.message || 'Не удалось загрузить активность');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (tab !== 'favourites' || favourites !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await learnerApi.userFavourites(userId);
        if (!cancelled) setFavourites(res.items);
      } catch (e: any) {
        if (!cancelled) toast.error(e?.response?.data?.message || 'Не удалось загрузить избранное');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, userId, favourites]);

  const formatDate = (s: string | null) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  if (!summary) {
    return (
      <div className={cn('text-center py-12 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
        No activity data.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard
          icon={ClipboardList}
          label="Attempts"
          value={summary.attempts_total}
          iconBg="#FBF3D9"
          iconColor="#9C7E1B"
          isDark={isDark}
        />
        <SummaryCard
          icon={Flame}
          label="Wrong total"
          value={summary.wrong_total}
          sublabel={`${summary.wrong_unique_questions} unique Qs`}
          iconBg="#FFE3E3"
          iconColor="#F15656"
          isDark={isDark}
        />
        <SummaryCard
          icon={Bookmark}
          label="Favourites"
          value={summary.favourites_count}
          iconBg="#EAF5EE"
          iconColor="#1F6F4A"
          isDark={isDark}
        />
      </div>

      {engagement && (
        <div className="grid grid-cols-4 gap-3">
          <StatCard
            label="Серия дней"
            value={engagement.games?.dailyStreak ?? 0}
            sublabel={`рекорд ${engagement.games?.dailyBestStreak ?? 0}`}
            isDark={isDark}
          />
          <StatCard
            label="Дуэли"
            value={engagement.games ? `${engagement.games.duelWins}/${engagement.games.duelLosses}/${engagement.games.duelDraws}` : '—'}
            sublabel={engagement.games ? `победы/поражения/ничьи · ELO ${engagement.games.eloRating}` : undefined}
            isDark={isDark}
          />
          <StatCard
            label="Игры: рекорды"
            value={engagement.games ? `${engagement.games.timeAttackBest} / ${engagement.games.survivalBest}` : '—'}
            sublabel="на время / выживание"
            isDark={isDark}
          />
          <StatCard
            label={`AI за ${engagement.ai.days} дн.`}
            value={engagement.ai.requests}
            sublabel={
              engagement.ai.lastUsedAt
                ? `${engagement.ai.activeDays} дн. · последний ${new Date(engagement.ai.lastUsedAt).toLocaleDateString('ru-RU')}`
                : 'не пользовался'
            }
            isDark={isDark}
          />
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList className={cn('rounded-xl p-1', isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]')}>
          <TabsTrigger value="summary" className="rounded-lg">Последние попытки</TabsTrigger>
          <TabsTrigger value="favourites" className="rounded-lg">Избранное</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'summary' && (
        <div>
          {summary.recent_attempts.length === 0 ? (
            <div className={cn('text-center py-8 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
              No attempts yet.
            </div>
          ) : (
            <div className="space-y-2">
              {summary.recent_attempts.map((a) => (
                <RecentAttemptRow key={a.id} attempt={a} formatDate={formatDate} isDark={isDark} />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'favourites' && (
        <div>
          {favourites === null ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#C9A227]" />
            </div>
          ) : favourites.length === 0 ? (
            <div className={cn('text-center py-8 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
              No favourites.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {favourites.map((f) => (
                <div
                  key={f.id}
                  className={cn(
                    'rounded-xl px-3 py-2 text-xs',
                    isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]'
                  )}
                >
                  <div className="font-mono font-semibold">Q#{f.question_id}</div>
                  <div className={cn('text-[10px] mt-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>
                    {formatDate(f.added_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface SummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  sublabel?: string;
  iconBg: string;
  iconColor: string;
  isDark: boolean;
}

function SummaryCard({ icon: Icon, label, value, sublabel, iconBg, iconColor, isDark }: SummaryCardProps) {
  return (
    <div className={cn(
      'rounded-2xl p-4',
      isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]'
    )}>
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: iconBg }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <span className={cn('text-xs font-semibold uppercase tracking-wider', isDark ? 'text-slate-400' : 'text-slate-500')}>
          {label}
        </span>
      </div>
      <div className={cn('text-2xl font-bold', isDark ? 'text-white' : 'text-[#111111]')}>
        {value.toLocaleString()}
      </div>
      {sublabel && (
        <div className={cn('text-[11px] mt-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>
          {sublabel}
        </div>
      )}
    </div>
  );
}

interface RecentAttemptRowProps {
  attempt: ExamAttemptListItem;
  formatDate: (s: string | null) => string;
  isDark: boolean;
}

function RecentAttemptRow({ attempt, formatDate, isDark }: RecentAttemptRowProps) {
  const score = attempt.total_questions > 0
    ? `${attempt.correct_count}/${attempt.total_questions}`
    : '—';

  return (
    <div className={cn(
      'flex items-center justify-between rounded-xl px-4 py-3 gap-4',
      isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]'
    )}>
      <div className="flex items-center gap-3 min-w-0">
        <Badge className="rounded-lg bg-[#FBF3D9] text-[#7A5E10] hover:bg-[#FBF3D9] capitalize">
          {attempt.kind}
        </Badge>
        {attempt.ref_id !== null && (
          <span className={cn('text-xs font-mono', isDark ? 'text-slate-400' : 'text-slate-500')}>
            #{attempt.ref_id}
          </span>
        )}
        <span className={cn('text-xs truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>
          {formatDate(attempt.started_at)}
        </span>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className={cn('text-sm font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
          {score}
        </span>
        {attempt.passed === true && (
          <Badge className="rounded-lg bg-[#EAF5EE] text-[#1F6F4A] hover:bg-[#EAF5EE]">Сдал</Badge>
        )}
        {attempt.passed === false && (
          <Badge className="rounded-lg bg-[#FFE3E3] text-[#9F1A1A] hover:bg-[#FFE3E3]">Не сдал</Badge>
        )}
        {attempt.abandoned && (
          <Badge variant="outline" className="rounded-lg">Брошено</Badge>
        )}
      </div>
    </div>
  );
}
